import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse,
  faUser,
  faUsers,
  faRightFromBracket,
  faArrowLeft,
  faUserShield,
  faArrowRight,
  faInfoCircle,
  faAngleDown,
  faAngleUp,
  faEllipsisV,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import Loader from "../components/Loader"; // Import Loader component

import BASE_URL from "../config";
import "../styles/Information.css";

const Users = () => {
  const [activeTab, setActiveTab] = useState("Credentials");
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [editedName, setEditedName] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [newInfo, setNewInfo] = useState({
    type: "credentials",
    name: "",
    content: "",
    created_by: null,
    client_id: "",
  });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const unauthenticated = localStorage.getItem("token") === null || localStorage.getItem("token") === "";
    if (unauthenticated) {
      window.location.href = "/login";
      return;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setNewInfo((prev) => ({ ...prev, created_by: payload.id }));
      } catch (error) {}
    }
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      setUsers([]);
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const endpoint = `${BASE_URL}api/information/find-by-type/${activeTab.toLowerCase()}`;
        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        setUsers(response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          setUsers([]);
        } else {
          Swal.fire("Error", error.response?.data?.message || `Failed to fetch ${activeTab}`, "error");
        }
      } finally {
        setLoading(false);
      }
    };

    setSearch("");
    fetchUsers();
  }, [activeTab]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${BASE_URL}api/clients/with-payments`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        setClients(response.data);
      } catch (error) {
        Swal.fire("Error", "Failed to fetch clients.", "error");
      }
    };

    fetchClients();
  }, []);

  const toggleCard = (cardId) => {
    setExpandedCard((prev) => (prev === cardId ? null : cardId));
  };

  const copyToClipboard = (name, content) => {
    const textToCopy = `${name}\n ${content}`;
    navigator.clipboard.writeText(textToCopy);
    Swal.fire({
      icon: "success",
      title: "Copied!",
      text: "Name and content have been copied to clipboard.",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), "MMMM dd, yyyy");
  };

  const progressColors = ["bg-danger", "bg-success", "bg-warning", "bg-info"];

  const renderProgressBar = (cardIndex) => {
    const color = progressColors[cardIndex % progressColors.length];
    return (
      <div className="progress" style={{ height: "5px", backgroundColor: "#ffffff" }}>
        <div
          className={`progress-bar ${color}`}
          role="progressbar"
          style={{ width: "25%" }}
          aria-valuenow="25"
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
    );
  };

  const deleteRecord = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const endpoint = `${BASE_URL}api/information/delete/${id}`;
        await axios.delete(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The record has been deleted successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
        setUsers(users.filter((user) => user.id !== id));
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || "Failed to delete the record.", "error");
      }
    } else {
      Swal.fire({
        icon: "info",
        title: "Cancelled",
        text: "The record was not deleted.",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const startEditing = (user) => {
    setEditingUser(user);
    setEditedName(user.name);
    setEditedContent(user.content);
  };

  const updateRecord = async () => {
    if (!editingUser) return;

    try {
      const token = localStorage.getItem("token");
      const endpoint = `${BASE_URL}api/information/update/${editingUser.id}`;
      await axios.put(
        endpoint,
        {
          ...editingUser,
          name: editedName,
          content: editedContent,
          updated_at: new Date().toISOString(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "The record has been updated successfully.",
        timer: 1500,
        showConfirmButton: false,
      });
      setUsers(
        users.map((user) =>
          user.id === editingUser.id
            ? { ...user, name: editedName, content: editedContent, updated_at: new Date().toISOString() }
            : user
        )
      );
      setEditingUser(null);
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Failed to update the record.", "error");
    }
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setNewInfo({ ...newInfo, [name]: value });
  };

  const addInformation = async () => {
    const errors = {};
    if (!newInfo.name.trim()) errors.name = "Name is required.";
    if (!newInfo.content.trim()) errors.content = "Content is required.";
    if (!newInfo.client_id.trim()) errors.client_id = "Client selection is required.";

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const endpoint = `${BASE_URL}api/information/create`;
      await axios.post(endpoint, newInfo, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      Swal.fire({
        icon: "success",
        title: "Added!",
        text: "Information has been added successfully.",
        timer: 1500,
        showConfirmButton: false,
      });
      setShowAddModal(false);
      setNewInfo({ type: "credentials", name: "", content: "", created_by: newInfo.created_by, client_id: "" });
      setValidationErrors({});
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Failed to add information.", "error");
    }
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
          <Loader message="Loading information..." />
        </div>
      );
    }

    const filteredUsers = users.filter(
      (user) =>
        user.name.toLowerCase().includes(search.toLowerCase()) ||
        user.content.toLowerCase().includes(search.toLowerCase())
    );

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3>{activeTab}</h3>
          <input
            type="text"
            className="form-control w-50"
            placeholder={`Search ${activeTab.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {filteredUsers.length === 0 ? (
          <p>No {activeTab.toLowerCase()} available.</p>
        ) : (
          <div className="row">
            {filteredUsers.map((user, index) => (
              <div className="col-md-4 mb-3" key={user.id}>
                <div className="card shadow-sm border-0 rounded" style={{ backgroundColor: "#ffffff" }}>
                  {renderProgressBar(index)}
                  <div className="d-flex justify-content-between align-items-center p-3">
                    <h5 className="mb-0">{user.name}</h5>
                    <div className="dropdown">
                      <button
                        className="btn btn-link text-dark"
                        type="button"
                        id={`dropdownMenuButton${user.id}`}
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <FontAwesomeIcon icon={faEllipsisV} size="lg" />
                      </button>
                      <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton${user.id}`}>
                        <li>
                          <button
                            className="dropdown-item"
                            onClick={() => startEditing(user)}
                          >
                            Edit
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item"
                            onClick={() => deleteRecord(user.id)}
                          >
                            Delete
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="card-body py-0">
                    <div
                      className="content-container"
                      style={{
                        maxHeight: expandedCard === user.id ? "500px" : "0",
                        overflow: "hidden",
                        transition: "max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          expandedCard === user.id
                            ? user.content
                            : ""
                        ),
                      }}
                    ></div>
                    {expandedCard === user.id && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          {user.creator?.username ? (
                            <a href={`${window.location.origin}/user-detail?id=${user.creator.id}`} title={user.creator.username}>
                              <img
                                src="https://lh3.googleusercontent.com/a/AGNmyxabdyjUhShMyEkxB96X86-qnHO-QF7SHvsrnQ49=s96-c"
                                alt="User"
                                className="rounded-circle me-2"
                                style={{ width: "40px", height: "40px" }}
                              />
                            </a>
                          ) : (
                            <img
                              src="https://lh3.googleusercontent.com/a/AGNmyxabdyjUhShMyEkxB96X86-qnHO-QF7SHvsrnQ49=s96-c"
                              alt="User"
                              className="rounded-circle me-2"
                              style={{ width: "40px", height: "40px" }}
                            />
                          )}
                          <FontAwesomeIcon
                            icon={faLink}
                            size="md"
                            className="ms-2 text-muted"
                            style={{ cursor: "pointer" }}
                            title="Copy name and content to clipboard"
                            onClick={() => copyToClipboard(user.name, user.content)}
                          />
                        </div>
                        <small className="text-muted">{formatDate(user.created_at)}</small>
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <button
                      className="btn btn-link"
                      type="button"
                      onClick={() => toggleCard(user.id)}
                    >
                      <FontAwesomeIcon icon={expandedCard === user.id ? faAngleUp : faAngleDown} size="lg" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mt-4" style={{ backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "8px" }}>
      <div className="mb-3 d-flex justify-content-between">
        <h2>Important Information</h2>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          Add Information
        </button>
      </div>
      <div className="mb-3 d-flex justify-content-between">
        <p>All important credentials, queries, links, stats, and reminders</p>
      </div>
      <ul className="nav nav-tabs mb-3 w-100 d-flex">
        {["Credentials", "Queries", "Links", "Stats", "Reminders"].map((tab) => (
          <li className="nav-item flex-grow-1 text-center" key={tab}>
            <button
              className={`nav-link w-100 ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-4">
        {renderTabContent()}
      </div>

      {editingUser && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Record</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingUser(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="editName" className="form-label">Name</label>
                  <input
                    type="text"
                    id="editName"
                    className="form-control"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="editContent" className="form-label">Content</label>
                  <textarea
                    id="editContent"
                    className="form-control"
                    rows="5"
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingUser(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={updateRecord}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Information</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="type" className="form-label">Type</label>
                  <select
                    id="type"
                    name="type"
                    className="form-select"
                    value={newInfo.type}
                    onChange={handleAddChange}
                  >
                    <option value="credentials">Credentials</option>
                    <option value="queries">Queries</option>
                    <option value="links">Links</option>
                    <option value="stats">Stats</option>
                    <option value="reminders">Reminders</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-control"
                    value={newInfo.name}
                    onChange={handleAddChange}
                    required
                  />
                  {validationErrors.name && <small className="text-danger">{validationErrors.name}</small>}
                </div>
                <div className="mb-3">
                  <label htmlFor="content" className="form-label">Content</label>
                  <textarea
                    id="content"
                    name="content"
                    className="form-control"
                    rows="5"
                    value={newInfo.content}
                    onChange={handleAddChange}
                    required
                  ></textarea>
                  {validationErrors.content && <small className="text-danger">{validationErrors.content}</small>}
                </div>
                <div className="mb-3">
                  <label htmlFor="client_id" className="form-label">Client</label>
                  <select
                    id="client_id"
                    name="client_id"
                    className="form-select"
                    value={newInfo.client_id}
                    onChange={handleAddChange}
                    required
                  >
                    <option value="">Select a client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  {validationErrors.client_id && <small className="text-danger">{validationErrors.client_id}</small>}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addInformation}
                >
                  Add Information
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
